import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Datastore, DatastoreCollection } from '@freelancer/datastore';
import {
  FreelancerReputation,
  LocalHourlyRate,
  OnlineOfflineCollection,
  PortfolioFileThumbnail,
  PortfolioItem,
  ProfileCategoriesCollection,
  ProfileViewUser,
  SkillsCollection,
} from '@freelancer/datastore/collections';
import { ModalService } from '@freelancer/ui';
import { CarouselImage } from '@freelancer/ui/carousel';
import {
  FileDisplayIconType,
  FileDisplayType,
} from '@freelancer/ui/file-display';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { ModalSize } from '@freelancer/ui/modal';
import { FontWeight } from '@freelancer/ui/text';
import { OnlineOfflineStatusApi } from 'api-typings/users/users';
import { HireMeUser } from 'app/hire-me/hire-me.types';
import {
  AwardProjectHireMeState,
  AwardProjectModel,
} from 'app/user-profile/user-profile-award-project.types';
import * as Rx from 'rxjs';
import { map, startWith, take } from 'rxjs/operators';
import { PortfolioItemSize } from '../user-portfolio.component';

@Component({
  selector: 'app-portfolio-item-card',
  template: `
    <fl-card
      flTrackingLabel="PortfolioItemCard"
      class="PortfolioItemCard"
      [edgeToEdge]="true"
      [maxContent]="false"
      [clickable]="showModalOnClick"
      (click)="handleClickEvent()"
    >
      <fl-bit
        class="PortfolioItemCard-file"
        flTrackingLabel="'PortfolioItemCard'"
      >
        <fl-carousel>
          <fl-carousel-prev-btn class="CarouselNav">
            <fl-icon
              [name]="'ui-arrow-left-alt'"
              [color]="IconColor.LIGHT"
              [size]="IconSize.SMALL"
            ></fl-icon>
          </fl-carousel-prev-btn>
          <fl-carousel-item
            *ngFor="let thumbnail of thumbnails; trackBy: trackByImageSource"
            class="UserPortfolioItemCard-item"
          >
            <ng-container
              *ngIf="!thumbnail.showGenericFileTypeIcon; else nonImage"
            >
              <fl-bit class="PortfolioItemCard-file-container">
                <fl-picture
                  class="PortfolioItemCard-file-image"
                  [alt]="thumbnail.alt"
                  [fixedAspectRatio]="true"
                  [externalSrc]="true"
                  [src]="thumbnail.src"
                  [lazyLoad]="true"
                ></fl-picture>
              </fl-bit>
            </ng-container>
            <ng-template #nonImage>
              <fl-bit class="PortfolioItemCard-file-nonImage">
                <fl-file-display
                  class="PortfolioItemCard-file-nonImage-display"
                  [alt]="thumbnail.alt"
                  [src]="thumbnail.src"
                  [iconType]="FileDisplayIconType.GENERIC"
                  [type]="FileDisplayType.ICON"
                  [iconColor]="IconColor.LIGHT"
                  [iconSize]="IconSize.XXLARGE"
                ></fl-file-display>
              </fl-bit>
            </ng-template>
            <fl-bit class="PortfolioItemCard-file-info">
              <fl-bit class="PortfolioItemCard-file-info-content">
                <fl-text
                  *ngIf="portfolioItem.title"
                  [weight]="FontWeight.MEDIUM"
                >
                  {{ portfolioItem.title }}
                </fl-text>
                <fl-text
                  i18n="Placeholder on portfolio card if it has no title"
                  *ngIf="!portfolioItem.title"
                  [weight]="FontWeight.MEDIUM"
                >
                  (untitled)
                </fl-text>
              </fl-bit>
            </fl-bit>
          </fl-carousel-item>
          <fl-carousel-next-btn class="CarouselNav">
            <fl-icon
              [name]="'ui-arrow-right-alt'"
              [color]="IconColor.LIGHT"
              [size]="IconSize.SMALL"
            ></fl-icon>
          </fl-carousel-next-btn>
        </fl-carousel>
      </fl-bit>
    </fl-card>
  `,
  styleUrls: ['./user-portfolio-item-card.component.scss'],
})
export class UserPortfolioItemCardComponent implements OnInit {
  FileDisplayIconType = FileDisplayIconType;
  FileDisplayType = FileDisplayType;
  FontWeight = FontWeight;
  IconColor = IconColor;
  IconSize = IconSize;
  PortfolioItemSize = PortfolioItemSize;

  // Ideal width for thumbnails to display in the gallery
  // see `ImageVersionHandler.php::$thumbnail_sizes` for possible dimensions
  readonly THUMBNAIL_WIDTH = 380;

  @Input() awardProjectBid$?: Rx.Observable<AwardProjectModel>;
  @Input() awardProjectHireMeState$?: Rx.Observable<AwardProjectHireMeState>;
  @Input()
  categoriesCollection: DatastoreCollection<ProfileCategoriesCollection>;
  @Input() hireMeUser$: Rx.Observable<HireMeUser>;
  @Input() isLoggedIn$: Rx.Observable<boolean>;
  @Input() localHourlyRate$: Rx.Observable<LocalHourlyRate | undefined>;
  @Input() portfolioItem: PortfolioItem;
  @Input() portfolioItemSize: PortfolioItemSize;
  @Input() reputation$: Rx.Observable<FreelancerReputation>;
  @Input() showModalOnClick = false;
  @Input() skillsCollection: DatastoreCollection<SkillsCollection>;
  @Input() user$: Rx.Observable<ProfileViewUser>;

  thumbnails: readonly CarouselImage[];
  isOnline$: Rx.Observable<boolean>;

  constructor(
    private modalService: ModalService,
    private datastore: Datastore,
    private router: Router,
  ) {}

  ngOnInit() {
    this.isOnline$ = this.datastore
      .document<OnlineOfflineCollection>(
        'onlineOffline',
        this.user$.pipe(map(user => user.id)),
      )
      .valueChanges()
      .pipe(
        map(status => status.status === OnlineOfflineStatusApi.ONLINE),
        startWith(false),
      );

    this.thumbnails = this.portfolioItem.files.map(file => {
      // Display the GIF (not the jpg thumbnails).
      if (file.filename.trim().endsWith('.gif')) {
        return {
          alt: this.portfolioItem.description,
          src: file.cdnUrl,
        };
      }

      if (file.thumbnails.length > 0) {
        return {
          alt: this.portfolioItem.description,
          src: this.getThumbnail(file.thumbnails, this.THUMBNAIL_WIDTH).cdnUrl,
        };
      }

      return {
        alt: this.portfolioItem.description,
        showGenericFileTypeIcon: true,
        src: file.cdnUrl,
      };
    });

    // set default thumbnail
    if (this.thumbnails.length === 0) {
      this.thumbnails = [{ src: '', showGenericFileTypeIcon: true }];
    }
  }

  /**
   * Defines the sort order of an array where the elements are sorted
   * descendingly by their `width` then their `height`.
   *
   * @param a Object that has a `width` and `height` property
   * @param b Object that has a `width` and `height` property
   */
  compareDimensions(
    a: { width?: number; height?: number },
    b: { width?: number; height?: number },
  ): number {
    if (!a.width || !a.height || !b.width || !b.height) {
      return -1;
    }

    if (a.width !== b.width) {
      return b.width - a.width;
    }

    return b.height - a.height;
  }

  /**
   * Returns the URL of the thumbnail whose dimensions match the given width.
   * If none matches, then return the URL of the largest thumbnail available.
   *
   * @param thumbnails  list of thumbnails to choose from
   * @param width  width of the thumbnail to get
   */
  getThumbnail(
    thumbnails: readonly PortfolioFileThumbnail[],
    width: number,
  ): PortfolioFileThumbnail {
    const sortedThumbs = [...thumbnails].sort(this.compareDimensions);

    let thumbnail = sortedThumbs.find(thumb => thumb.width === width);

    if (!thumbnail) {
      // assign first element (largest) in sorted thumbs
      [thumbnail] = sortedThumbs;
    }

    return thumbnail;
  }

  handleClickEvent() {
    Rx.combineLatest([this.isLoggedIn$, this.user$])
      .pipe(take(1))
      .toPromise()
      .then(([isLoggedIn, user]) => {
        // If the user is logged in, redirect to the offline portfolio page.
        if (!isLoggedIn && this.showModalOnClick) {
          this.router.navigate([
            '/u',
            user.username,
            'portfolio',
            this.portfolioItem.seoTitle,
          ]);

          // Return to ensure the modal opening is not executed before the
          // routing has been completed.
          return;
        }

        if (this.showModalOnClick) {
          this.modalService
            .open('UserPortfolioItemOverlay', {
              edgeToEdge: true,
              inputs: {
                awardProjectBid$: this.awardProjectBid$,
                awardProjectHireMeState$: this.awardProjectHireMeState$,
                categoriesCollection: this.categoriesCollection,
                hireMeUser$: this.hireMeUser$,
                isOnline$: this.isOnline$,
                localHourlyRate$: this.localHourlyRate$,
                portfolioItem$: Rx.of(this.portfolioItem),
                reputation$: this.reputation$,
                skillsCollection: this.skillsCollection,
                user$: this.user$,
              },
              mobileFullscreen: true,
              size: ModalSize.LARGE,
            })
            .afterClosed()
            .toPromise()
            .then(response => {
              if (response && response.projectLink && response.bidId) {
                this.router.navigate([response.projectLink], {
                  fragment: 'proposals',
                  queryParams: {
                    awardBid: response.bidId,
                  },
                });
              }
            });
        }
      });
  }

  trackByImageSource(_: number, image: CarouselImage) {
    return image.src;
  }
}
